import React, { useState, useEffect, useRef } from "react";
import AdminLayout from "../UI/Layout/AdminLayout";
import Axios from "axios";
import { NODE_HOST_URL_v1 } from "../Dashboard/api/Api";
import { MdOutlineModeEditOutline } from "react-icons/md";
import { IoMdClose, IoMdArrowDropdown } from "react-icons/io";
import { HiOutlineTrash } from "react-icons/hi";
import { FiFilter, FiSearch } from "react-icons/fi";
import { AiOutlineInfoCircle } from "react-icons/ai";
import Select from "react-select";
import styles from "./AdminEventPanel.module.css";


const StatusBadge = ({ status }) => (
    <span className={`${styles.statusBadge} ${status === 1 ? styles.statusActive : styles.statusInactive}`}>
        {status === 1 ? "Active" : "Inactive"}
    </span>
);


const AdminEventPanel = () => {
    // ---------------- USER STATE ----------------
    const lStorage =
        JSON.parse(localStorage.getItem("admin")) ||
        JSON.parse(localStorage.getItem("emp")) ||
        JSON.parse(localStorage.getItem("reseller"));

    const userId = lStorage?.user?.userId;
    const userToken = lStorage?.token;
    const type = lStorage?.user?.isAdmin;

    // ---------------- FORM STATE ----------------
    const [eventName, setEventName] = useState("");
    const [selectedPlatformId, setSelectedPlatformId] = useState("");
    const [eventStatus, setEventStatus] = useState(1);

    // Add modal
    const [showAddModal, setShowAddModal] = useState(false);

    // Edit modal
    const [editingId, setEditingId] = useState(null);
    const [editingEventName, setEditingEventName] = useState("");
    const [editingPlatformId, setEditingPlatformId] = useState("");
    const [editingStatus, setEditingStatus] = useState(1);
    const [showEditModal, setShowEditModal] = useState(false);

    const [confirmDelete, setConfirmDelete] = useState({
        show: false,
        id: null,
        name: ""
    });

    const [eventList, setEventList] = useState([]);
    const [platformList, setPlatformList] = useState([]);
    const [loading, setLoading] = useState(false);

    // Accordion state - track which platforms are expanded
    const [expandedPlatforms, setExpandedPlatforms] = useState(new Set());

    // Filter state
    const [showFilterDropdown, setShowFilterDropdown] = useState(false);
    const [visiblePlatforms, setVisiblePlatforms] = useState(new Set());
    const filterRef = useRef(null);

    // ---------------- UNIFIED SEARCH STATE ----------------
    const [searchQuery, setSearchQuery] = useState("");

    // ---------------- BULK EVENT PREVIEW ----------------
    const [parsedEvents, setParsedEvents] = useState([]);

    const [error, setError] = useState({
        successMessage: false,
        errorMessage: "",
    });

    const platformOptions = platformList.map(p => ({
        value: p.id,
        label: p.plat_name
    }));

    // auto-hide alert
    useEffect(() => {
        if (error.errorMessage) {
            const timer = setTimeout(() =>
                setError({ ...error, errorMessage: "" }), 5000
            );
            return () => clearTimeout(timer);
        }
    }, [error]);

    // Parse comma-separated event names
    useEffect(() => {
        if (eventName.trim()) {
            // Split by comma and clean up each event name
            const events = eventName
                .split(',')
                .map(e => e.trim())
                .filter(e => e.length > 0);
            
            setParsedEvents(events);
        } else {
            setParsedEvents([]);
        }
    }, [eventName]);

    // Group events by platform for count badges
    const eventsByPlatform = eventList.reduce((acc, event) => {
        if (!acc[event.plat_id]) {
            acc[event.plat_id] = [];
        }
        acc[event.plat_id].push(event);
        return acc;
    }, {});

    // Filter platforms that have events
    const platformsWithEvents = platformList.filter(platform => 
        eventsByPlatform[platform.id] && eventsByPlatform[platform.id].length > 0
    );

    // Initialize visible platforms when platforms with events change
    useEffect(() => {
        const allPlatformIds = new Set(platformsWithEvents.map(p => p.id));
        setVisiblePlatforms(allPlatformIds);
    }, [platformsWithEvents.length]);

    // ---------------- UNIFIED SEARCH FILTERING ----------------
    // Filter platforms and events by unified search query
    const searchFilteredPlatforms = platformsWithEvents.filter(platform => {
        if (!searchQuery) return true;
        
        const query = searchQuery.toLowerCase();
        
        // Check if platform name matches
        const platformMatches = platform.plat_name.toLowerCase().includes(query);
        
        // Check if any events in this platform match
        const platformEvents = eventsByPlatform[platform.id] || [];
        const hasMatchingEvent = platformEvents.some(event =>
            event.event_name.toLowerCase().includes(query)
        );
        
        return platformMatches || hasMatchingEvent;
    });

    // Filter events by search query within each platform
    const getFilteredEventsForPlatform = (platformId) => {
        const platformEvents = eventsByPlatform[platformId] || [];
        if (!searchQuery) return platformEvents;
        
        return platformEvents.filter(event =>
            event.event_name.toLowerCase().includes(searchQuery.toLowerCase())
        );
    };

    // Filtered platforms based on visibility and search
    const displayedPlatforms = searchFilteredPlatforms.filter(platform => 
        visiblePlatforms.has(platform.id)
    );

    // Auto-expand platforms when searching
    useEffect(() => {
        if (searchQuery) {
            // Expand all visible platforms that have matching events
            const platformsToExpand = new Set();
            displayedPlatforms.forEach(platform => {
                const filteredEvents = getFilteredEventsForPlatform(platform.id);
                if (filteredEvents.length > 0) {
                    platformsToExpand.add(platform.id);
                }
            });
            setExpandedPlatforms(platformsToExpand);
        }
    }, [searchQuery]);

    // Toggle accordion
    const toggleAccordion = (platformId) => {
        const newExpanded = new Set(expandedPlatforms);
        if (newExpanded.has(platformId)) {
            newExpanded.delete(platformId);
        } else {
            newExpanded.add(platformId);
        }
        setExpandedPlatforms(newExpanded);
    };

    // Toggle platform visibility
    const togglePlatformVisibility = (platformId) => {
        const newVisible = new Set(visiblePlatforms);
        if (newVisible.has(platformId)) {
            newVisible.delete(platformId);
        } else {
            newVisible.add(platformId);
        }
        setVisiblePlatforms(newVisible);
    };

    // Select all platforms
    const selectAllPlatforms = () => {
        const allPlatformIds = new Set(platformsWithEvents.map(p => p.id));
        setVisiblePlatforms(allPlatformIds);
    };

    // Deselect all platforms
    const deselectAllPlatforms = () => {
        setVisiblePlatforms(new Set());
    };

    // ---------------- SUBMIT (CREATE EVENT - BULK) ----------------
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!eventName.trim() || !selectedPlatformId) {
            return setError({
                successMessage: false,
                errorMessage: "Please fill required fields.",
            });
        }

        if (parsedEvents.length === 0) {
            return setError({
                successMessage: false,
                errorMessage: "Please enter at least one event name.",
            });
        }

        const form = e.target;
        setLoading(true);

        try {
            const payload = {
                method: "InsertEvent",
                user_id: userId,
                user_type: type,
                token: userToken,
                plat_id: Number(selectedPlatformId),
                event_name: parsedEvents, // Send array of event names
            };

            const { data } = await Axios.post(`${NODE_HOST_URL_v1}/admin/DataReceiver`, payload);

            if (data.success) {
                setError({
                    successMessage: true,
                    errorMessage: parsedEvents.length > 1 
                        ? `${parsedEvents.length} events created successfully!`
                        : "Event created successfully!",
                });

                await fetchList();
                
                // Auto-expand the platform where events were added
                setExpandedPlatforms(new Set([...expandedPlatforms, Number(selectedPlatformId)]));
                
                // Make sure platform is visible
                setVisiblePlatforms(new Set([...visiblePlatforms, Number(selectedPlatformId)]));
                
                setShowAddModal(false);
            } else {
                setError({
                    successMessage: false,
                    errorMessage: data.message || "Event creation failed.",
                });
            }

            // reset inputs
            setEventName("");
            setSelectedPlatformId("");
            setEventStatus(1);
            setParsedEvents([]);
            form.reset();

        } catch (err) {
            setError({
                successMessage: false,
                errorMessage: err?.response?.data?.message || "Something went wrong!",
            });
        }

        setLoading(false);
    };

    // ---------------- DELETE EVENT ----------------
    const deleteEvent = async () => {
        setLoading(true);
        try {
            const body = {
                method: "DeleteEvent",
                user_id: userId,
                token: userToken,
                user_type: type,
                id: confirmDelete.id,
            };

            const { data } = await Axios.post(`${NODE_HOST_URL_v1}/admin/DataReceiver`, body);

            if (data.success) {
                setError({
                    successMessage: true,
                    errorMessage: "Event deleted successfully!",
                });

                setEventList(prev => prev.filter(item => item.id !== confirmDelete.id));
            } else {
                setError({
                    successMessage: false,
                    errorMessage: data.message || "Deletion failed.",
                });
            }

        } catch (error) {
            setError({
                successMessage: false,
                errorMessage: error?.response?.data?.message || "Delete failed!",
            });
        }

        setConfirmDelete({ show: false, id: null, name: "" });
        setLoading(false);
    };

    // ---------------- UPDATE EVENT ----------------
    const updateEvent = async (id) => {
        if (!editingEventName.trim()) return alert("Event name cannot be empty");

        setLoading(true);
        try {
            const body = {
                method: "UpdateEvent",
                user_id: userId,
                token: userToken,
                user_type: type,
                id,
                plat_id: Number(editingPlatformId),
                event_name: editingEventName,
                status: editingStatus
            };

            const { data } = await Axios.post(`${NODE_HOST_URL_v1}/admin/DataReceiver`, body);

            if (data.success) {
                setError({
                    successMessage: true,
                    errorMessage: "Event updated successfully!",
                });

                setEventList(prev =>
                    prev.map(item =>
                        item.id === id
                            ? { ...item, event_name: editingEventName, status: editingStatus, plat_id: editingPlatformId }
                            : item
                    )
                );

                setShowEditModal(false);
                setEditingId(null);
            } else {
                setError({
                    successMessage: false,
                    errorMessage: data.message || "Update failed.",
                });
            }
        } catch (error) {
            setError({
                successMessage: false,
                errorMessage: error?.response?.data?.message || "Update failed!",
            });
        }

        setLoading(false);
    };

    // ---------------- FETCH EVENTS ----------------
    const fetchList = async () => {
        setLoading(true);
        try {
            const body = {
                method: "FetchEvent",
                user_id: userId,
                user_type: type,
                token: userToken,
            };

            const { data } = await Axios.post(`${NODE_HOST_URL_v1}/admin/DataReceiver`, body);

            if (data.success && Array.isArray(data.data)) {
                setEventList(data.data);
            } else {
                setError({
                    successMessage: false,
                    errorMessage: data.message || "Failed to fetch list",
                });
            }
        } catch (error) {
            setError({
                successMessage: false,
                errorMessage: "Failed to load event list.",
            });
        }
        setLoading(false);
    };

    // ---------------- FETCH PLATFORMS ----------------
    const fetchPlatformList = async () => {
        try {
            const body = {
                method: "ListPlatform",
                user_id: userId,
                user_type: type,
                token: userToken,
            };

            const { data } = await Axios.post(`${NODE_HOST_URL_v1}/admin/DataReceiver`, body);

            if (data.success) {
                setPlatformList(data.data);
            }
        } catch (error) {
            setError({
                successMessage: false,
                errorMessage: error?.response?.data?.message || "something went wrong",
            });
        }
    };

    useEffect(() => {
        if (userId) {
            fetchPlatformList();
            fetchList();
        }
    }, [userId]);

    // Custom styles for react-select
    const selectStyles = {
        control: (base, state) => ({
            ...base,
            padding: '0.25rem',
            border: state.isFocused ? '1.5px solid #3b82f6' : '1.5px solid #e2e8f0',
            borderRadius: '8px',
            boxShadow: state.isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
            '&:hover': {
                border: '1.5px solid #3b82f6'
            }
        }),
        menu: (base) => ({
            ...base,
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            zIndex: 9999
        })
    };

    // ---------------------------------------------------------------------
    //                              UI START
    // ---------------------------------------------------------------------
    return (
        <AdminLayout>
            <div className={styles.pageContainer}>

                {/* PAGE TITLE */}
                <div className={styles.pageHeader}>
                    <h1 className={styles.pageTitle}>Event Management</h1>
                    
                    <div className={styles.headerActions}>
                        {/* Unified Search Bar */}
                        <div className={styles.searchWrapper}>
                            <FiSearch className={styles.searchIcon} size={20} />
                            <input
                                type="text"
                                className={styles.searchInput}
                                placeholder="Search platforms or events..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            {searchQuery && (
                                <button
                                    className={styles.searchClearBtn}
                                    onClick={() => setSearchQuery("")}
                                >
                                    <IoMdClose size={18} />
                                </button>
                            )}
                        </div>

                        {/* Filter Button */}
                        <div className={styles.filterContainer} ref={filterRef}>
                            <button 
                                className={`${styles.filterButton} ${showFilterDropdown ? styles.filterButtonActive : ''}`}
                                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                            >
                                <FiFilter size={18} />
                                Filter
                                {visiblePlatforms.size > 0 && visiblePlatforms.size < platformsWithEvents.length && (
                                    <span className={styles.filterCount}>{visiblePlatforms.size}</span>
                                )}
                            </button>

                            {/* Filter Dropdown */}
                            {showFilterDropdown && (
                                <>
                                    <div 
                                        className={styles.filterOverlay} 
                                        onClick={() => setShowFilterDropdown(false)}
                                    />
                                    <div className={styles.filterDropdown}>
                                        <div className={styles.filterDropdownHeader}>
                                            <h4 className={styles.filterDropdownTitle}>Show/Hide Platforms</h4>
                                            <div className={styles.filterActions}>
                                                <button 
                                                    className={styles.filterActionBtn}
                                                    onClick={selectAllPlatforms}
                                                >
                                                    Select All
                                                </button>
                                                <button 
                                                    className={styles.filterActionBtn}
                                                    onClick={deselectAllPlatforms}
                                                >
                                                    Clear All
                                                </button>
                                            </div>
                                        </div>

                                        <div className={styles.filterDropdownBody}>
                                            {platformsWithEvents.map(platform => (
                                                <div 
                                                    key={platform.id}
                                                    className={styles.filterOption}
                                                    onClick={() => togglePlatformVisibility(platform.id)}
                                                >
                                                    <div className={`${styles.filterCheckbox} ${visiblePlatforms.has(platform.id) ? styles.filterCheckboxChecked : ''}`} />
                                                    <div className={styles.filterLabel}>
                                                        <span className={styles.filterPlatformName}>
                                                            {platform.plat_name}
                                                        </span>
                                                        <span className={styles.filterEventCount}>
                                                            {eventsByPlatform[platform.id]?.length || 0}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        <div className={styles.filterDropdownFooter}>
                                            <span>{visiblePlatforms.size} of {platformsWithEvents.length} platforms visible</span>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        <button className={styles.addButton} onClick={() => setShowAddModal(true)}>
                            + Add Event
                        </button>
                    </div>
                </div>

                {/* SEARCH RESULTS INFO */}
                {searchQuery && (
                    <div className={styles.searchResultsInfo}>
                        {displayedPlatforms.length === 0 ? (
                            <span className={styles.noResults}>
                                No platforms or events found matching "{searchQuery}"
                            </span>
                        ) : (
                            <span className={styles.resultsCount}>
                                Found {displayedPlatforms.length} platform{displayedPlatforms.length !== 1 ? 's' : ''} matching "{searchQuery}"
                            </span>
                        )}
                    </div>
                )}

                {/* ALERTS */}
                {error.errorMessage && (
                    <div className={`${styles.alert} ${error.successMessage ? styles.alertSuccess : styles.alertError}`}>
                        <button
                            className={styles.closeBtn}
                            onClick={() => setError({ ...error, errorMessage: "" })}
                        >
                            <IoMdClose size={20} />
                        </button>
                        {error.errorMessage}
                    </div>
                )}

                {/* LOADING STATE */}
                {loading && eventList.length === 0 && (
                    <div className={styles.loadingState}>
                        <div className={styles.spinner}></div>
                        <div className={styles.loadingText}>Loading events...</div>
                    </div>
                )}

                {/* ACCORDION - Only show if not loading or has data */}
                {(!loading || eventList.length > 0) && (
                    <div className={styles.accordionContainer}>
                        
                        {/* Show message if no events or all filtered out */}
                        {displayedPlatforms.length === 0 && !loading && (
                            <div className={styles.emptyState}>
                                {platformsWithEvents.length === 0 
                                    ? "No events found. Click 'Add Event' to create one."
                                    : searchQuery
                                        ? `No platforms or events match "${searchQuery}"`
                                        : "No platforms selected. Use the filter button to show platforms."
                                }
                            </div>
                        )}

                        {/* Platform Accordions */}
                        {displayedPlatforms.map(platform => {
                            const isExpanded = expandedPlatforms.has(platform.id);
                            const filteredEvents = getFilteredEventsForPlatform(platform.id);
                            const totalEvents = eventsByPlatform[platform.id]?.length || 0;

                            return (
                                <div key={platform.id} className={styles.accordionItem}>
                                    
                                    {/* Accordion Header */}
                                    <button
                                        className={`${styles.accordionHeader} ${isExpanded ? styles.accordionHeaderActive : ''}`}
                                        onClick={() => toggleAccordion(platform.id)}
                                    >
                                        <div className={styles.accordionHeaderLeft}>
                                            <div className={`${styles.accordionIcon} ${isExpanded ? styles.accordionIconActive : ''}`}>
                                                <IoMdArrowDropdown size={24} />
                                            </div>
                                            <h3 className={`${styles.accordionTitle} ${isExpanded ? styles.accordionTitleActive : ''}`}>
                                                {platform.plat_name}
                                            </h3>
                                        </div>
                                        <span className={`${styles.accordionBadge} ${isExpanded ? styles.accordionBadgeActive : ''}`}>
                                            {searchQuery && filteredEvents.length !== totalEvents
                                                ? `${filteredEvents.length}/${totalEvents}` 
                                                : filteredEvents.length
                                            }
                                        </span>
                                    </button>

                                    {/* Accordion Content */}
                                    <div className={`${styles.accordionContent} ${isExpanded ? styles.accordionContentExpanded : ''}`}>
                                        <div className={styles.accordionBody}>
                                            {filteredEvents.length === 0 ? (
                                                <div className={styles.noEventsMessage}>
                                                    {searchQuery 
                                                        ? `No events match "${searchQuery}" in this platform`
                                                        : "No events in this platform"
                                                    }
                                                </div>
                                            ) : (
                                                <div className={styles.tableWrapper}>
                                                    <table className={styles.table}>
                                                        <thead>
                                                            <tr>
                                                                <th>Event Name</th>
                                                                <th>Status</th>
                                                                <th>Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {filteredEvents.map((item) => (
                                                                <tr key={item.id}>
                                                                    <td className={styles.eventName}>
                                                                        {item.event_name}
                                                                    </td>
                                                                    <td>
                                                                        <StatusBadge status={item.status} />
                                                                    </td>
                                                                    <td>
                                                                        <div className={styles.actionButtons}>
                                                                            <button
                                                                                className={styles.btnEdit}
                                                                                onClick={() => {
                                                                                    setEditingId(item.id);
                                                                                    setEditingEventName(item.event_name);
                                                                                    setEditingPlatformId(item.plat_id);
                                                                                    setEditingStatus(item.status);
                                                                                    setShowEditModal(true);
                                                                                }}
                                                                            >
                                                                                <MdOutlineModeEditOutline size={16} />
                                                                                Edit
                                                                            </button>

                                                                            <button
                                                                                className={styles.btnDelete}
                                                                                onClick={() =>
                                                                                    setConfirmDelete({
                                                                                        show: true,
                                                                                        id: item.id,
                                                                                        name: item.event_name,
                                                                                    })
                                                                                }
                                                                            >
                                                                                <HiOutlineTrash size={16} />
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                </div>
                            );
                        })}
                    </div>
                )}

            </div>

            {/* ADD EVENT MODAL - WITH BULK SUPPORT */}
            {showAddModal && (
                <div className={styles.modalOverlay} onClick={() => setShowAddModal(false)}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>

                        <div className={styles.modalHeader}>
                            <h3 className={styles.modalTitle}>Add New Event(s)</h3>

                            <button
                                onClick={() => setShowAddModal(false)}
                                className={styles.modalCloseBtn}
                            >
                                <IoMdClose size={20} />
                            </button>
                        </div>

                        <div className={styles.modalBody}>

                            <form onSubmit={handleSubmit}>

                                {/* Platform Dropdown */}
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Platform</label>
                                    <Select
                                        options={platformOptions}
                                        value={platformOptions.find(o => o.value === Number(selectedPlatformId)) || null}
                                        onChange={(option) => setSelectedPlatformId(option?.value || "")}
                                        placeholder="Select platform..."
                                        styles={selectStyles}
                                    />
                                </div>

                                {/* Event Name - Bulk Input */}
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>
                                        Event Name(s)
                                        <span className={styles.bulkInfoIcon}>
                                            <AiOutlineInfoCircle size={16} />
                                        </span>
                                    </label>
                                    <textarea
                                        className={styles.formTextarea}
                                        value={eventName}
                                        onChange={(e) => setEventName(e.target.value)}
                                        placeholder="Enter event names separated by commas&#10;Example: Event 1, Event 2, Event 3"
                                        rows={4}
                                    />
                                    <div className={styles.bulkHint}>
                                        <AiOutlineInfoCircle size={14} />
                                        <span>Separate multiple event names with commas for bulk creation</span>
                                    </div>
                                </div>

                                {/* Preview Parsed Events */}
                                {parsedEvents.length > 0 && (
                                    <div className={styles.previewContainer}>
                                        <div className={styles.previewHeader}>
                                            <span className={styles.previewTitle}>
                                                Events to be created: 
                                            </span>
                                            <span className={styles.previewCount}>
                                                {parsedEvents.length}
                                            </span>
                                        </div>
                                        <div className={styles.previewList}>
                                            {parsedEvents.map((event, index) => (
                                                <div key={index} className={styles.previewItem}>
                                                    <span className={styles.previewNumber}>{index + 1}.</span>
                                                    <span className={styles.previewEventName}>{event}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    className={styles.btnPrimary}
                                    disabled={loading}
                                    style={{ width: '100%' }}
                                >
                                    {loading && (
                                        <div 
                                            className={styles.spinner} 
                                            style={{
                                                width: '1rem',
                                                height: '1rem',
                                                borderWidth: '2px',
                                                margin: 0
                                            }}
                                        ></div>
                                    )}
                                    {loading 
                                        ? "Creating..." 
                                        : parsedEvents.length > 1 
                                            ? `Create ${parsedEvents.length} Events` 
                                            : "Create Event"
                                    }
                                </button>

                            </form>

                        </div>

                    </div>
                </div>
            )}

            {/* EDIT EVENT MODAL */}
            {showEditModal && (
                <div className={styles.modalOverlay} onClick={() => { setShowEditModal(false); setEditingId(null); }}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>

                        <div className={styles.modalHeader}>
                            <h3 className={styles.modalTitle}>Edit Event</h3>
                            <button
                                className={styles.modalCloseBtn}
                                onClick={() => setShowEditModal(false)}
                            >
                                <IoMdClose size={20} />
                            </button>
                        </div>

                        <div className={styles.modalBody}>

                            {/* Platform */}
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Platform</label>
                                <Select
                                    options={platformOptions}
                                    value={platformOptions.find(o => o.value === Number(editingPlatformId)) || null}
                                    onChange={(option) => setEditingPlatformId(option?.value || "")}
                                    placeholder="Select platform..."
                                    styles={selectStyles}
                                />
                            </div>

                            {/* Event Name */}
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Event Name</label>
                                <input
                                    type="text"
                                    className={styles.formInput}
                                    value={editingEventName}
                                    onChange={(e) => setEditingEventName(e.target.value)}
                                />
                            </div>

                            {/* Status */}
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Status</label>
                                <select
                                    className={styles.formSelect}
                                    value={editingStatus}
                                    onChange={(e) => setEditingStatus(Number(e.target.value))}
                                >
                                    <option value={1}>Active</option>
                                    <option value={2}>Inactive</option>
                                </select>
                            </div>

                        </div>

                        <div className={styles.modalFooter}>
                            <button
                                className={styles.btnSecondary}
                                onClick={() => setShowEditModal(false)}
                            >
                                Cancel
                            </button>

                            <button
                                className={styles.btnPrimary}
                                disabled={loading}
                                onClick={() => updateEvent(editingId)}
                            >
                                {loading && (
                                    <div 
                                        className={styles.spinner} 
                                        style={{
                                            width: '1rem',
                                            height: '1rem',
                                            borderWidth: '2px',
                                            margin: 0
                                        }}
                                    ></div>
                                )}
                                {loading ? "Saving..." : "Save Changes"}
                            </button>
                        </div>

                    </div>
                </div>
            )}

            {/* DELETE CONFIRM MODAL */}
            {confirmDelete.show && (
                <div
                    className={styles.modalOverlay}
                    onClick={() => setConfirmDelete({ show: false })}
                >
                    <div
                        className={`${styles.modalContent} ${styles.deleteModalContent}`}
                        onClick={(e) => e.stopPropagation()}
                    >

                        {/* Header */}
                        <div className={styles.modalHeader}>
                            <h3 className={styles.modalTitle}>Confirm Deletion</h3>

                            <button
                                type="button"
                                onClick={() => setConfirmDelete({ show: false })}
                                className={styles.modalCloseBtn}
                            >
                                <IoMdClose size={20} />
                            </button>
                        </div>

                        {/* Body */}
                        <div className={styles.deleteModalBody}>
                            <p className={styles.deleteModalText}>
                                Are you sure you want to delete:
                            </p>

                            <p className={styles.deleteModalName}>
                                {confirmDelete.name}
                            </p>

                            <p className={styles.deleteModalWarning}>
                                This action cannot be undone.
                            </p>
                        </div>

                        {/* Footer */}
                        <div className={styles.modalFooter}>
                            <button
                                className={styles.btnSecondary}
                                onClick={() => setConfirmDelete({ show: false })}
                            >
                                Cancel
                            </button>

                            <button
                                className={styles.btnDanger}
                                disabled={loading}
                                onClick={deleteEvent}
                            >
                                <HiOutlineTrash size={16} />
                                {loading ? "Deleting..." : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </AdminLayout>
    );
};


export default AdminEventPanel;
